import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Plus, X, ArrowRight, GripVertical } from 'lucide-react';

export default function SectorsPage() {
  const [sectors, setSectors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newSub, setNewSub] = useState({});  // { sectorName: 'inputValue' }
  const [movingItem, setMovingItem] = useState(null); // { from: sector, sub: name }

  const handleUploadExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sector-config/upload`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Uploaded: ${JSON.stringify(data.sectors)}`);
        // Reload
        const r = await api.getSectorConfig();
        setSectors(r.data.sectors || {});
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  useEffect(() => {
    api.getSectorConfig()
      .then(res => setSectors(res.data.sectors || {}))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSectorConfig(sectors);
      toast.success('Sector configuration saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const addSubsector = (sector) => {
    const val = (newSub[sector] || '').trim();
    if (!val) return;
    if ((sectors[sector] || []).includes(val)) { toast.error('Already exists'); return; }
    setSectors(prev => ({
      ...prev,
      [sector]: [...(prev[sector] || []), val],
    }));
    setNewSub(prev => ({ ...prev, [sector]: '' }));
  };

  const removeSubsector = (sector, sub) => {
    setSectors(prev => ({
      ...prev,
      [sector]: (prev[sector] || []).filter(s => s !== sub),
    }));
  };

  const moveSubsector = (targetSector) => {
    if (!movingItem) return;
    const { from, sub } = movingItem;
    if (from === targetSector) { setMovingItem(null); return; }
    setSectors(prev => ({
      ...prev,
      [from]: (prev[from] || []).filter(s => s !== sub),
      [targetSector]: [...(prev[targetSector] || []), sub],
    }));
    setMovingItem(null);
    toast.success(`Moved "${sub}" to ${targetSector}`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const sectorOrder = ['Energy', 'Utilities', 'Transportation', 'Digital', 'Social/Public'];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="sectors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight">Sector Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage OLTA Sectors and their Subsectors</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <Button disabled={uploading} variant="outline" className="border-border text-foreground rounded-sm text-sm h-9 px-4" asChild>
              <span>{uploading ? 'Uploading...' : 'Upload Excel'}</span>
            </Button>
            <input type="file" accept=".xlsx" className="hidden" onChange={handleUploadExcel} />
          </label>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9 px-4">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save Changes
          </Button>
        </div>
      </div>

      {movingItem && (
        <div className="bg-blue-950/20 border border-blue-800/30 rounded-sm p-3 flex items-center gap-3">
          <ArrowRight className="w-4 h-4 text-blue-400" />
          <span className="text-sm text-blue-400">Moving <strong>"{movingItem.sub}"</strong> from {movingItem.from} — click a target sector below</span>
          <Button onClick={() => setMovingItem(null)} size="sm" variant="ghost" className="text-xs h-6">Cancel</Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sectorOrder.map(sector => {
          const subs = sectors[sector] || [];
          const isTarget = movingItem && movingItem.from !== sector;

          return (
            <div key={sector}
              onClick={() => isTarget ? moveSubsector(sector) : null}
              className={`bg-card border rounded-sm p-4 transition-colors ${
                isTarget ? 'border-blue-500 cursor-pointer hover:bg-blue-950/10' : 'border-border'
              }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm text-foreground font-medium">{sector}</h3>
                <span className="text-[10px] text-muted-foreground font-mono">{subs.length}</span>
              </div>

              <div className="space-y-1 mb-3">
                {subs.sort().map(sub => (
                  <div key={sub} className="flex items-center gap-2 group py-0.5">
                    <button onClick={(e) => { e.stopPropagation(); setMovingItem({ from: sector, sub }); }}
                      title="Move to another sector"
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-blue-400 transition-all cursor-grab">
                      <GripVertical className="w-3 h-3" />
                    </button>
                    <span className="text-xs text-foreground flex-1">{sub}</span>
                    <button onClick={(e) => { e.stopPropagation(); removeSubsector(sector, sub); }}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-red-400 transition-all">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {subs.length === 0 && <p className="text-[10px] text-muted-foreground/40 py-2 text-center">No subsectors</p>}
              </div>

              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                <Input value={newSub[sector] || ''} onChange={e => setNewSub(prev => ({ ...prev, [sector]: e.target.value }))}
                  placeholder="New subsector..."
                  className="bg-secondary border-border rounded-sm h-7 text-[11px] flex-1"
                  onKeyDown={e => e.key === 'Enter' && addSubsector(sector)} />
                <Button onClick={() => addSubsector(sector)} size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
